import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { cityFromGen, defaultDestinationFor, fixPolishGrammar } from "@/lib/seo-routes";
import { useEffect } from "react";
import { SeoPageView } from "@/components/seo-page-view";
import { airportByLowerCode } from "@/lib/seo-routes";
import { AIRPORTS, countryFromSlug, type Airport } from "@/lib/airports";
import { loadSeoPage, buildSeoPageHead, triggerSeoGeneration } from "@/lib/seo-page-loader";
import { buildSearchHref } from "@/lib/search-href";
import { supabase } from "@/integrations/supabase/client";
import { curatedCityContent } from "@/lib/curated-city-guides";
import { breadcrumbScript } from "@/lib/breadcrumbs";
import { faqScript } from "@/lib/faq-schema";

export const Route = createFileRoute("/loty/$city")({
  loader: async ({ params }) => {
    let a = airportByLowerCode(params.city);
    if (!a) {
      // Slug kraju (np. /loty/wielka-brytania) → strona kraju, nie geo-hub.
      if (countryFromSlug(params.city)) {
        throw redirect({ to: "/loty/kraj/$slug", params: { slug: params.city }, statusCode: 301 });
      }
      throw notFound();
    }
    const [page, dynamicDest] = await Promise.all([
      curatedCityContent(a.code) ?? loadSeoPage({ type: "city", from: a.code, fromName: a.city }),
      // Czytamy realny top-1 kierunek z DB (uzupełniany cron-em raz w miesiącu).
      // Fallback do hardcoded mapy obsłużony przez defaultDestinationFor poniżej.
      (async () => {
        try {
          const { data } = await supabase
            .from("city_top_destinations")
            .select("dest_iata")
            .eq("city_iata", a.code)
            .eq("rank", 1)
            .maybeSingle();
          return data?.dest_iata ?? null;
        } catch {
          return null;
        }
      })(),
    ]);
    const to = dynamicDest ?? defaultDestinationFor(a.code);
    const today = new Date(); today.setUTCHours(0,0,0,0);
    const dd = new Date(today.getTime() + 30 * 86400_000).toISOString().slice(0, 10);
    const rd = new Date(today.getTime() + 37 * 86400_000).toISOString().slice(0, 10);
    return { a, page, dd, rd, to };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return { meta: [] };
    const { a, page } = loaderData;
    const path = `/loty/${a.code.toLowerCase()}`;
    const head = buildSeoPageHead(
      page,
      path,
      `Tanie loty z ${cityFromGen(a.city)} (${a.code})`,
      `Wszystkie tanie kierunki lotów z ${cityFromGen(a.city)}. Porównaj ceny biletów do całego świata.`
    );
    const breadcrumb = breadcrumbScript([
      { name: "Strona główna", path: "/" },
      { name: "Loty", path: "/loty" },
      { name: fixPolishGrammar(`Loty z ${cityFromGen(a.city)}`), path },
    ]);
    const faqLd = page?.faq ? faqScript(page.faq) : null;
    const scripts = [...(head.scripts ?? []), breadcrumb];
    if (faqLd) scripts.push(faqLd);
    return { ...head, scripts };
  },
  component: CityPage,
});

function CityPage() {
  const { a, page, dd, rd, to } = Route.useLoaderData();
  useEffect(() => {
    if (!page) triggerSeoGeneration({ type: "city", from: a.code, fromName: a.city });
  }, [page, a.code, a.city]);
  return (
    <SeoPageView
      type="city"
      from={a.code}
      fromName={a.city}
      page={page}
      dd={dd}
      rd={rd}
      adults={1}
      fallbackH1={(() => {
        // Gdy w tym samym mieście jest > 1 lotnisko (np. Warszawa: WAW+WMI),
        // dopinamy nazwę portu (Modlin/Chopina), żeby user wiedział, na które
        // lotnisko trafił. Dla miast z jednym lotniskiem (Kraków, Gdańsk)
        // zostaje czysty „Tanie loty z {miasta}".
        const sameCity = AIRPORTS.filter(
          (x: Airport) => x.city === a.city && !x.isMetro && !x.isCountry,
        );
        const suffix = sameCity.length > 1 && a.name && a.name !== "Wszystkie lotniska" ? ` ${a.name}` : "";
        return `Tanie loty z ${cityFromGen(a.city)}${suffix}`;
      })()}
      defaultTo={to}
      searchHref={buildSearchHref({ from: a.code, to, dd, rd, adults: 1 })}
    />
  );
}
